import type {
  ChannelType,
  Market,
  Merchant,
  MerchantRegion,
  RateRule,
  StoreChannel,
} from '../dataProvider';
import type { ResolutionTarget } from './resolveEffectiveRate';

/**
 * Catalog assembly + scope matching at the REGION grain, for the scope-count
 * preview (D-rate-rules). A scope preview counts the STORES (merchant_regions) a
 * rule's scope affects, per market. A channel-shaped scope (marketplace,
 * marketplace_category, tag_marketplaces) counts a region that HAS a matching
 * channel. This is a real filter over the catalog the multi-channel lead
 * established, never a faked number.
 */

/** One store (merchant_region) flattened with everything a scope can match on. */
export interface RegionScopeView {
  regionId: number;
  market: Market;
  /** The brand's category (a brand-level hop: categoryId lives on the merchant). */
  categoryId: number;
  tagKeys: string[];
  /** The region baseRate (the base for a region-level resolution). */
  baseRate: number;
  channels: { channelType: ChannelType; baseRate: number }[];
}

/** Assemble the region views from the catalog collections (a pure join). */
export const buildRegionViews = (
  regions: readonly MerchantRegion[],
  merchants: readonly Merchant[],
  channels: readonly StoreChannel[],
): RegionScopeView[] => {
  const categoryByMerchant = new Map(
    merchants.map((m) => [m.id, m.categoryId]),
  );
  const channelsByRegion = new Map<number, StoreChannel[]>();
  for (const c of channels) {
    const list = channelsByRegion.get(c.merchantRegionId) ?? [];
    list.push(c);
    channelsByRegion.set(c.merchantRegionId, list);
  }
  return (
    regions
      .map((r) => ({
        regionId: r.id,
        market: r.market,
        categoryId: categoryByMerchant.get(r.merchantId) ?? -1,
        tagKeys: (r.tagKeys as string[]) ?? [],
        baseRate: r.baseRate,
        channels: (channelsByRegion.get(r.id) ?? []).map((c) => ({
          channelType: c.channelType,
          baseRate: c.baseRate,
        })),
      }))
      // Stable ascending order so the conflict preview's "first in-scope store" is
      // deterministic regardless of the provider's read order.
      .sort((a, b) => a.regionId - b.regionId)
  );
};

/** Does the rule's scope match this store (region grain)? Marketplace scopes match
 *  a region that carries a channel of that type. */
export const scopeMatchesRegion = (
  rule: RateRule,
  view: RegionScopeView,
): boolean => {
  const ref = rule.scopeRef;
  const channelTypes = view.channels.map((c) => c.channelType);
  switch (rule.scopeType) {
    case 'store':
      return ref === view.regionId;
    case 'marketplace':
      return channelTypes.includes(ref as ChannelType);
    case 'category':
      return ref === view.categoryId;
    case 'marketplace_category': {
      const r = ref as { channelType: ChannelType; categoryId: number };
      return (
        channelTypes.includes(r.channelType) && r.categoryId === view.categoryId
      );
    }
    case 'tag':
      return view.tagKeys.includes(ref as string);
    case 'tag_marketplaces': {
      const r = ref as { tag: string; marketplaces: ChannelType[] };
      return (
        view.tagKeys.includes(r.tag) &&
        r.marketplaces.some((m) => channelTypes.includes(m))
      );
    }
    case 'store_list':
      return (ref as number[]).includes(view.regionId);
    case 'market':
      return ref === view.market;
    default:
      return false;
  }
};

export interface ScopeCount {
  total: number;
  byMarket: Partial<Record<Market, number>>;
}

/**
 * Count the stores a rule's scope affects, per market. The rule's market scope
 * (uk/us/ie/all) still applies, so an 'all' rule counts across markets while a uk
 * rule counts only uk stores.
 */
export const countScopeByMarket = (
  rule: RateRule,
  views: readonly RegionScopeView[],
): ScopeCount => {
  const byMarket: Partial<Record<Market, number>> = {};
  let total = 0;
  for (const view of views) {
    const marketOk = rule.market === view.market || rule.market === 'all';
    if (marketOk && scopeMatchesRegion(rule, view)) {
      byMarket[view.market] = (byMarket[view.market] ?? 0) + 1;
      total += 1;
    }
  }
  return { total, byMarket };
};

/** A region-level resolution target (no specific channel; base = region baseRate). */
export const regionTarget = (view: RegionScopeView): ResolutionTarget => ({
  regionId: view.regionId,
  market: view.market,
  categoryId: view.categoryId,
  tagKeys: view.tagKeys,
  channelType: null,
  baseRate: view.baseRate,
});

/** A channel-level resolution target (base = the channel baseRate). */
export const channelTarget = (
  view: RegionScopeView,
  channel: { channelType: ChannelType; baseRate: number },
): ResolutionTarget => ({
  regionId: view.regionId,
  market: view.market,
  categoryId: view.categoryId,
  tagKeys: view.tagKeys,
  channelType: channel.channelType,
  baseRate: channel.baseRate,
});

/**
 * The resolution target a rule's scope implies for a store: a channel-shaped
 * scope (marketplace / marketplace_category / tag_marketplaces) resolves the
 * matching CHANNEL on the store; every other scope resolves the store region.
 * Used by the conflict preview to resolve a sample store the way the rule bites.
 */
export const targetForRule = (
  rule: RateRule,
  view: RegionScopeView,
): ResolutionTarget => {
  let channelType: ChannelType | null = null;
  if (rule.scopeType === 'marketplace') {
    channelType = rule.scopeRef as ChannelType;
  } else if (rule.scopeType === 'marketplace_category') {
    channelType = (rule.scopeRef as { channelType: ChannelType }).channelType;
  } else if (rule.scopeType === 'tag_marketplaces') {
    const ms = (rule.scopeRef as { marketplaces: ChannelType[] }).marketplaces;
    channelType =
      view.channels.find((c) => ms.includes(c.channelType))?.channelType ??
      null;
  }
  if (channelType != null) {
    const ch = view.channels.find((c) => c.channelType === channelType);
    if (ch) return channelTarget(view, ch);
  }
  return regionTarget(view);
};
